

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ChevronDown, ChevronUp, User } from 'lucide-react';

interface InterviewSession {
  id: string;
  candidate_name: string;
  candidate_email: string;
  user_id?: string;
  status: string;
  started_at: string;
  ended_at: string;
  duration_seconds: number;
  transcript: string;
  ai_evaluation: {
    finalScore?: number;
    feedbackTitle?: string;
    communication?: number;
    confidence?: number;
    problemSolving?: number;
    technicalKnowledge?: number;
    professionalism?: number;
    experience?: number;
    eyeContact?: number;
    posture?: number;
    attire?: number;
    lighting?: number;
    background?: number;
    detailedFeedback?: string;
    overall_score?: number;
    content_score?: number;
    behaviour_score?: number;
    grade?: string;
    feedback?: string;
  };
  job_posting: {
    title: string;
    company_name: string;
  };
}

export default function InterviewResultsPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [session, setSession] = useState<InterviewSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);

  useEffect(() => {
    if (params.id) {
      fetchSession();
    }
  }, [params.id]);

  const fetchSession = async () => {
    try {
      const { interviewSessionsApi } = await import('@/services/api');
      const data = await interviewSessionsApi.getById(params.id!);
      setSession(data.session);
    } catch (error) {
      console.error('Error fetching session:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-sky-600 mx-auto mb-4"></div>
          <p className="text-slate-600 text-lg">Loading results...</p>
        </div>
      </div>
    );
  }

  if (!session || !session.ai_evaluation || Object.keys(session.ai_evaluation).length === 0) {
    const isIncomplete = session?.status === 'incomplete';
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            {isIncomplete ? 'Interview Too Short' : 'No Results Available'}
          </h2>
          <p className="text-slate-600 mb-6">
            {isIncomplete
              ? 'This interview ended before reaching 50% completion. Scores are only calculated for interviews that are at least halfway complete. Your coins were still deducted for the time used.'
              : 'This interview has not been evaluated yet.'}
          </p>
          <Button onClick={() => navigate('/interviews')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Interviews
          </Button>
        </div>
      </div>
    );
  }

  const { ai_evaluation } = session;
  const finalScore = ai_evaluation.finalScore || ai_evaluation.overall_score || 0;
  const feedbackTitle = ai_evaluation.feedbackTitle || 'Interview Analysis Complete';
  const communication = ai_evaluation.communication || 50;
  const confidence = ai_evaluation.confidence || 50;
  const problemSolving = ai_evaluation.problemSolving || 50;
  const technicalKnowledge = ai_evaluation.technicalKnowledge || 50;
  const professionalism = ai_evaluation.professionalism || 50;
  const experience = ai_evaluation.experience || 50;
  const detailedFeedback = ai_evaluation.detailedFeedback || ai_evaluation.feedback || '';

  const metrics = [
    { label: 'Communication', score: communication, fullMark: 100 },
    { label: 'Confidence', score: confidence, fullMark: 100 },
    { label: 'Problem Solving', score: problemSolving, fullMark: 100 },
    { label: 'Technical', score: technicalKnowledge, fullMark: 100 },
    { label: 'Professionalism', score: professionalism, fullMark: 100 },
    { label: 'Experience', score: experience, fullMark: 100 }
  ];

  const contentScore = Math.round((communication + problemSolving + technicalKnowledge + experience) / 4);
  const behaviourScore = Math.round((confidence + professionalism) / 2);

  const getStars = (score: number) => {
    const stars = Math.round((score / 100) * 5);
    return '★'.repeat(stars) + '☆'.repeat(5 - stars);
  };

  const getStarColor = (label: string) => {
    const colors: Record<string, string> = {
      'Communication': 'text-sky-500',
      'Confidence': 'text-emerald-500',
      'Problem Solving': 'text-sky-500',
      'Technical Knowledge': 'text-indigo-500',
      'Professionalism': 'text-blue-500',
      'Experience': 'text-pink-500'
    };
    return colors[label] || 'text-slate-500';
  };

  return (
    <div className="min-h-screen bg-slate-100 overflow-x-hidden">
      <style>{`
        .progress-ring__circle {
          stroke-dasharray: 377;
          stroke-dashoffset: 377;
          transform-origin: center;
          transform: rotate(-90deg);
          animation: radial-progress 1.1s ease-out forwards;
        }
        @keyframes radial-progress {
          to {
            stroke-dashoffset: calc(377 - (3.77 * var(--percent)));
          }
        }
        .score-bar-inner {
          width: var(--target-width);
          animation: grow-bar 1s ease-out forwards;
          animation-delay: var(--delay, 0s);
        }
        @keyframes grow-bar {
          from { width: 0; }
          to { width: var(--target-width); }
        }
        .chart-bar {
          transform-origin: bottom;
          transform: scaleY(0);
          animation: grow-vert 0.9s ease-out forwards;
          animation-delay: var(--delay, 0s);
        }
        @keyframes grow-vert {
          to { transform: scaleY(1); }
        }
      `}</style>

      <div className="w-full px-4 py-6 md:py-10">
        <div className="w-full bg-white rounded-3xl shadow-xl p-6 md:p-8 space-y-6 overflow-hidden">

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center text-sm font-semibold text-slate-600">
                {getInitials(session.candidate_name)}
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-lg font-semibold text-slate-900">{session.candidate_name}</h1>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 border border-sky-100">
                    {session.job_posting.title}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Completed {formatDate(session.ended_at)} • {formatDuration(session.duration_seconds)} duration
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
                <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                Completed
              </span>

              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/interviews')}
                className="rounded-full"
              >
                <ArrowLeft className="w-3 h-3 mr-1" />
                Back
              </Button>

              {session.user_id && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => navigate(`/profile/${session.user_id}`)}
                  className="rounded-full"
                >
                  <User className="w-3 h-3 mr-1" />
                  View Profile
                </Button>
              )}
            </div>
          </div>

          {/* Large Feedback Title */}
          <div className="text-center py-4">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">{feedbackTitle}</h2>
          </div>

          {/* Three Main Cards */}
          <div className="grid gap-6 grid-cols-1 lg:grid-cols-3">

            {/* CARD 1: Overall Score + Content vs Behaviour */}
            <article className="bg-white rounded-3xl shadow-sm p-6 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">Overall Score</h2>
                  <p className="text-xs text-slate-500">
                    Final 0–100 score based on all 6 metrics.
                  </p>
                </div>
                <span className="text-[11px] px-3 py-1 rounded-full bg-slate-100 text-slate-600">
                  AI-evaluated
                </span>
              </div>

              <div className="mt-4 flex items-center gap-6">
                {/* Left: donut + chips */}
                <div className="flex flex-col items-center gap-3">
                  <div className="relative w-32 h-32">
                    <svg className="w-full h-full" viewBox="0 0 160 160">
                      <circle
                        className="text-slate-100"
                        strokeWidth="14"
                        stroke="currentColor"
                        fill="transparent"
                        r="60"
                        cx="80"
                        cy="80"
                      />
                      <circle
                        className="text-sky-500 progress-ring__circle"
                        strokeWidth="14"
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="transparent"
                        r="60"
                        cx="80"
                        cy="80"
                        style={{ '--percent': finalScore } as any}
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <p className="text-xl font-semibold">{Math.round(finalScore)}%</p>
                      <p className="text-[11px] text-slate-500">Final score</p>
                    </div>
                  </div>

                  <div className="flex gap-2 text-[11px]">
                    <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-violet-50 text-violet-700">
                      <span className="w-2 h-2 rounded-full bg-violet-500"></span>
                      Content {contentScore}
                    </div>
                    <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">
                      <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                      Behaviour {behaviourScore}
                    </div>
                  </div>
                </div>

                {/* Right: simple numeric summary */}
                <div className="flex-1 space-y-2 text-xs">
                  <p className="text-[11px] text-slate-400 uppercase">This interview</p>
                  <p className="text-3xl font-semibold leading-none">{Math.round(finalScore)}</p>
                  <p className="text-[11px] text-slate-500">
                    Out of 100 for this session.
                  </p>
                </div>
              </div>

              <p className="mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-500 leading-snug">
                <span className="font-medium">Content</span> combines Communication, Problem Solving,
                Technical Knowledge and Experience. <span className="font-medium">Behaviour</span> combines
                Confidence and Professionalism.
              </p>
            </article>

            {/* CARD 2: Radar – all 6 metrics with stars */}
            <article className="bg-white rounded-3xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold">Performance Metrics</h2>
                  <p className="text-xs text-slate-500">Assessment across all 6 metrics (0–100).</p>
                </div>
                <span className="text-[11px] text-slate-400">0–5 stars</span>
              </div>

              <div className="space-y-4">
                {metrics.map((metric) => (
                  <div key={metric.label}>
                    <div className="flex justify-between mb-1">
                      <span className="font-medium">{metric.label}</span>
                      <span className="text-slate-600">{Math.round(metric.score)}</span>
                    </div>
                    <div className={`flex ${getStarColor(metric.label)} text-lg leading-none`}>
                      {getStars(metric.score)}
                    </div>
                  </div>
                ))}
              </div>

              <p className="mt-4 pt-3 border-t text-[11px] text-slate-500 leading-snug">
                These ratings reflect your performance across all measured interview skills.
              </p>
            </article>

            {/* CARD 3: Behaviour & video analysis */}
            <article className="bg-white rounded-3xl shadow-sm p-6 flex flex-col gap-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">AI Video Analysis</h2>
                  <p className="text-xs text-slate-500">Behaviour scoring based on your video frames.</p>
                </div>
                <span className="text-[11px] text-sky-600">Video-based</span>
              </div>

              {/* CONFIDENCE */}
              <section>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium">Confidence</span>
                  <span className="font-semibold text-slate-900">{Math.round(confidence)}</span>
                </div>
                <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden mb-3">
                  <div className="h-full rounded-full bg-emerald-500 score-bar-inner" style={{ '--target-width': `${confidence}%`, '--delay': '0s' } as any}></div>
                </div>

                <div className="space-y-2 text-[11px] text-slate-600">
                  <div className="flex justify-between">
                    <span>Eye contact</span>
                    <span className="font-semibold">{Math.round(session.ai_evaluation.eyeContact ?? Math.max(60, confidence - 5))}</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-400 score-bar-inner" style={{ '--target-width': `${session.ai_evaluation.eyeContact ?? Math.max(60, confidence - 5)}%`, '--delay': '0.1s' } as any}></div>
                  </div>

                  <div className="flex justify-between">
                    <span>Posture & stillness</span>
                    <span className="font-semibold">{Math.round(session.ai_evaluation.posture ?? Math.max(55, confidence - 10))}</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-400 score-bar-inner" style={{ '--target-width': `${session.ai_evaluation.posture ?? Math.max(55, confidence - 10)}%`, '--delay': '0.2s' } as any}></div>
                  </div>

                  <div className="flex justify-between">
                    <span>Tone & energy</span>
                    <span className="font-semibold">{Math.round(confidence)}</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-400 score-bar-inner" style={{ '--target-width': `${confidence}%`, '--delay': '0.3s' } as any}></div>
                  </div>
                </div>
              </section>

              {/* PROFESSIONALISM */}
              <section className="pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium">Professionalism</span>
                  <span className="font-semibold text-slate-900">{Math.round(professionalism)}</span>
                </div>
                <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden mb-3">
                  <div className="h-full rounded-full bg-sky-500 score-bar-inner" style={{ '--target-width': `${professionalism}%`, '--delay': '0s' } as any}></div>
                </div>

                <div className="space-y-2 text-[11px] text-slate-600">
                  <div className="flex justify-between">
                    <span>Attire & grooming</span>
                    <span className="font-semibold">{Math.round(session.ai_evaluation.attire ?? Math.max(60, professionalism - 5))}</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-sky-400 score-bar-inner" style={{ '--target-width': `${session.ai_evaluation.attire ?? Math.max(60, professionalism - 5)}%`, '--delay': '0.1s' } as any}></div>
                  </div>

                  <div className="flex justify-between">
                    <span>Background & lighting</span>
                    <span className="font-semibold">{Math.round((session.ai_evaluation.background && session.ai_evaluation.lighting) ? (session.ai_evaluation.background + session.ai_evaluation.lighting) / 2 : professionalism + 2)}</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-sky-400 score-bar-inner" style={{ '--target-width': `${(session.ai_evaluation.background && session.ai_evaluation.lighting) ? (session.ai_evaluation.background + session.ai_evaluation.lighting) / 2 : professionalism + 2}%`, '--delay': '0.2s' } as any}></div>
                  </div>

                  <div className="flex justify-between">
                    <span>Distraction level</span>
                    <span className="font-semibold">{Math.round(session.ai_evaluation.background ?? Math.min(95, professionalism + 10))}</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-sky-400 score-bar-inner" style={{ '--target-width': `${session.ai_evaluation.background ?? Math.min(95, professionalism + 10)}%`, '--delay': '0.3s' } as any}></div>
                  </div>
                </div>
              </section>
            </article>

          </div>

          {/* Detailed Feedback Section */}
          <div className="bg-slate-50 rounded-2xl p-6 shadow-sm border border-slate-100">
            <h3 className="text-base font-semibold text-slate-900 mb-4">{feedbackTitle}</h3>
            <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
              {detailedFeedback}
            </div>
          </div>

          {/* Transcript Section */}
          {session.transcript && (
            <div className="bg-slate-50 rounded-2xl p-6 shadow-sm border border-slate-100">
              <button
                onClick={() => setTranscriptExpanded(!transcriptExpanded)}
                className="flex items-center justify-between w-full text-left"
              >
                <h3 className="text-base font-semibold text-slate-900">Interview Transcript</h3>
                {transcriptExpanded ? (
                  <ChevronUp className="w-5 h-5 text-slate-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-500" />
                )}
              </button>

              {transcriptExpanded && (
                <div className="mt-4 space-y-3 max-h-96 overflow-y-auto">
                  {session.transcript.split('\n').map((line, index) => {
                    const isInterviewer = line.toLowerCase().startsWith('interviewer:') || line.toLowerCase().startsWith('ai interviewer:');
                    const isCandidate = line.toLowerCase().startsWith('candidate:') || line.toLowerCase().startsWith('you:');

                    if (isInterviewer) {
                      return (
                        <div key={index} className="flex">
                          <div className="bg-blue-100 text-blue-900 rounded-2xl px-4 py-2.5 inline-block max-w-[85%] text-sm">
                            {line}
                          </div>
                        </div>
                      );
                    } else if (isCandidate) {
                      return (
                        <div key={index} className="flex justify-end">
                          <div className="bg-green-100 text-green-900 rounded-2xl px-4 py-2.5 inline-block max-w-[85%] text-sm">
                            {line}
                          </div>
                        </div>
                      );
                    } else if (line.trim()) {
                      return (
                        <div key={index} className="text-sm text-slate-600">
                          {line}
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
